import { SyncErrorIcon } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import useStationResetById from '@/features/config/hooks/station/useStationResetById'
import { usePermission } from '@/hooks/permissions'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

import diagnosticsApi from '@/api/operations/diagnosticsApi'

interface SoilTableProps {
  rows: any
}

const LOCAL_KEY_ERROR = 'id-error'

const SoilPopupTable: FC<SoilTableProps> = ({ rows }) => {
  const { t } = useTranslation()

  const themeMode = useSelector(selectTheme)
  const { mutate: resetById, isSuccess } = useStationResetById()
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const [arrayReset, setArrayReset] = useState<any>([])
  const [diagnoseLoading, setDiagnoseLoading] = useState<number[]>([])
  const [diagnosticsData, setDiagnosticsData] = useState<{
    [key: number]: { description?: string; reason: string; action: string }
  }>({})
  const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)

  console.log(rows)

  const handleResetId = (id: any) => {
    // Check if user is ViewOnly
    if (isViewOnly) return

    if (getKeyReset === null || !getKeyReset?.includes(id)) {
      resetById({
        interfaceId: interfaceId,
        id: id,
      })
      const arrayNew = [...arrayReset, id]
      setArrayReset(arrayNew)
      if (!isSuccess) {
        setTimeout(function () {
          setArrayReset([])
        }, 2000)
      }
    }
  }

  const handleDiagnose = async (id: number) => {
    // Mark this row as loading
    setDiagnoseLoading((prev) => (prev.includes(id) ? prev : [...prev, id]))
    try {
      const res = await diagnosticsApi.runDiagnostic({
        interfaceId: Number(interfaceId),
        lastStationId: 0,
        stationIds: [id],
      })
      const data = res.data
      if (Array.isArray(data) && data.length > 0) {
        const description = data[0]?.error?.description ?? ''
        const reason = data[0]?.error?.reason ?? ''
        const action = data[0]?.error?.action ?? ''
        setDiagnosticsData((prev) => ({
          ...prev,
          [id]: { description, reason, action },
        }))
      }
    } finally {
      // Clear loading state for this row
      setDiagnoseLoading((prev) => prev.filter((x) => x !== id))
    }
  }

  useEffect(() => {
    const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)
    if (getKeyReset) {
      setArrayReset(JSON.parse(getKeyReset))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ minWidth: 450 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ paddingLeft: '22px' }}>
                {t('tableHeaderId')}
              </TableCell>
              <TableCell>{t('adminStationNameLabel')}</TableCell>
              <TableCell>{t('decoderAddress')}</TableCell>
              <TableCell>{t('errorCode')}</TableCell>
              <TableCell>{t('errorMessage')}</TableCell>
              <TableCell>{t('diagnosticReason')}</TableCell>
              <TableCell>{t('suggestedAction')}</TableCell>
              <TableCell>{t('interfaceTableHeaderAction')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: 'common.white',
              '& .MuiTableRow-root:nth-of-type(even)': {
                backgroundColor: '#F9F9F9',
              },
            }}
          >
            {rows.map((row: any) => (
              <TableRow key={row.id}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ paddingLeft: '22px' }}
                >
                  {row.id}
                </TableCell>
                <TableCell>{row.controller}</TableCell>
                <TableCell>{`${row.address}-${row.index}`}</TableCell>
                <TableCell>{row.errorCode}</TableCell>
                <TableCell sx={{ color: '#FF3030' }}>
                  {(() => {
                    const diag = diagnosticsData[row.id]
                    const desc = diag?.description?.trim()
                    return desc && desc.length > 0 ? desc : row.errorName
                  })()}
                </TableCell>
                <TableCell>
                  {diagnosticsData[row.id]
                    ? diagnosticsData[row.id].reason &&
                      diagnosticsData[row.id].reason.trim().length > 0
                      ? diagnosticsData[row.id].reason
                      : '-'
                    : row.reason || ''}
                </TableCell>
                <TableCell>
                  {diagnosticsData[row.id]
                    ? diagnosticsData[row.id].action &&
                      diagnosticsData[row.id].action.trim().length > 0
                      ? diagnosticsData[row.id].action
                          .split('\n')
                          .map((line: string, idx: number) => (
                            <div
                              key={idx}
                              style={{ fontSize: 12, lineHeight: 1.6 }}
                            >
                              {line}
                            </div>
                          ))
                      : '-'
                    : (row.suggestedAction || '')
                        .split('\n')
                        .map((line: string, idx: number) => (
                          <div
                            key={idx}
                            style={{ fontSize: 12, lineHeight: 1.6 }}
                          >
                            {line}
                          </div>
                        ))}
                </TableCell>
                <TableCell>
                  <Stack
                    spacing={3}
                    direction="row"
                    className={classes.txtErTable}
                    sx={{
                      opacity: isViewOnly ? 0.5 : 1,
                      cursor: isViewOnly ? 'not-allowed' : 'pointer',
                      pointerEvents: isViewOnly ? 'none' : 'auto',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: themeMode === mode_type.dark ? '#fff' : '#000',
                        cursor: 'pointer',
                        mr: 2,
                        pointerEvents: diagnoseLoading.includes(row.id)
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <LoadingButton
                        loading={diagnoseLoading.includes(row.id)}
                        loadingPosition="start"
                        startIcon={<SyncErrorIcon />}
                        onClick={() => handleDiagnose(row.id)}
                      >
                        {t('errors.diagnose')}
                      </LoadingButton>
                    </Box>
                    <Box
                      component="span"
                      className={classes.txtBox}
                      onClick={() => handleResetId(row.id)}
                      sx={{
                        color:
                          themeMode === mode_type.dark
                            ? '#fff !important'
                            : '#000 !important',
                      }}
                    >
                      {arrayReset?.includes(row.id) ? (
                        <LoadingButton
                          loading
                          loadingPosition="start"
                          startIcon={<SyncErrorIcon />}
                        >
                          {t('errors.acknowledge')}
                        </LoadingButton>
                      ) : (
                        <>
                          <SyncErrorIcon sx={{ marginRight: '4px' }} />{' '}
                          {t('errors.acknowledge')}
                        </>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default SoilPopupTable
